import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, FileCheck, Microscope } from "lucide-react";

const QA_METRICS = [
  { label: "Tests Passed", value: "99.7%", color: "text-ib-lime" },
  { label: "Avg Response Time", value: "< 24hrs", color: "text-ib-blue" },
  { label: "Defect Rate", value: "0.03%", color: "text-ib-lime" },
  { label: "Certifications", value: "ISO 9001", color: "text-ib-blue" },
];

const QA_STEPS = [
  { icon: Microscope, title: "Material Testing", desc: "Chemical composition, tensile strength, and hardness verified at source." },
  { icon: FileCheck, title: "Documentation", desc: "Mill test certificates, heat numbers, and compliance reports digitized." },
  { icon: CheckCircle2, title: "Automated QC", desc: "AI-powered dimensional inspection and surface defect detection." },
  { icon: AlertTriangle, title: "Deviation Alerts", desc: "Real-time notifications for any spec deviations before shipment." },
];

export function QualityAssurance() {
  return (
    <section id="solutions" data-testid="qa-section" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="font-jetbrains text-xs text-ib-lime tracking-widest uppercase">
            Quality Assurance
          </span>
          <h2 className="font-outfit font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-3 text-ib-text">
            Automated <span className="text-ib-blue">QA/QC</span> Reporting
          </h2>
          <p className="font-manrope text-base md:text-lg text-ib-text-secondary mt-4 max-w-2xl mx-auto">
            Every gram verified. Every document digitized. Our automated quality
            assurance pipeline ensures your materials meet exact specifications.
          </p>
        </motion.div>

        {/* Metrics bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="glass rounded-xl p-6 mb-12 grid grid-cols-2 md:grid-cols-4 gap-6"
          data-testid="qa-metrics"
        >
          {QA_METRICS.map((m) => (
            <div key={m.label} className="text-center">
              <div className={`font-jetbrains text-2xl font-bold ${m.color}`}>{m.value}</div>
              <div className="font-manrope text-xs text-ib-text-muted mt-1">{m.label}</div>
            </div>
          ))}
        </motion.div>

        {/* QA Process steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {QA_STEPS.map((step) => {
            const Icon = step.icon;
            const stepIdx = QA_STEPS.indexOf(step);
            return (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: stepIdx * 0.1 }}
                className="bento-card rounded-lg border border-ib-steel/20 bg-ib-surface/50 p-6 relative"
                data-testid={`qa-step-${stepIdx}`}
              >
                {/* Step number */}
                <span className="absolute top-4 right-4 font-jetbrains text-[10px] text-ib-text-muted">
                  0{stepIdx + 1}
                </span>
                <div className="inline-flex p-2.5 rounded-lg bg-ib-blue/10 mb-4">
                  <Icon className="w-5 h-5 text-ib-blue" />
                </div>
                <h3 className="font-outfit font-bold text-base text-ib-text mb-2">{step.title}</h3>
                <p className="font-manrope text-sm text-ib-text-secondary leading-relaxed">{step.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Sample report preview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 rounded-xl border border-ib-steel/20 bg-ib-surface/30 overflow-hidden"
          data-testid="qa-report-preview"
        >
          <div className="px-6 py-3 border-b border-ib-steel/10 flex items-center gap-3">
            <div className="flex gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-ib-negative/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-ib-warning/60" />
              <div className="w-2.5 h-2.5 rounded-full bg-ib-lime/60" />
            </div>
            <span className="font-jetbrains text-[10px] text-ib-text-muted">QA_REPORT_IB-2024-0892.json</span>
          </div>
          <div className="p-6 font-jetbrains text-xs leading-loose">
            <span className="text-ib-text-muted">{"{"}</span><br />
            <span className="text-ib-text-muted ml-4">"material":</span>{" "}
            <span className="text-ib-lime">"Hot Rolled Steel HRC"</span>,<br />
            <span className="text-ib-text-muted ml-4">"grade":</span>{" "}
            <span className="text-ib-lime">"IS 2062 E250"</span>,<br />
            <span className="text-ib-text-muted ml-4">"thickness_mm":</span>{" "}
            <span className="text-ib-blue">12.05</span>,<br />
            <span className="text-ib-text-muted ml-4">"tensile_strength_mpa":</span>{" "}
            <span className="text-ib-blue">420</span>,<br />
            <span className="text-ib-text-muted ml-4">"yield_strength_mpa":</span>{" "}
            <span className="text-ib-blue">250</span>,<br />
            <span className="text-ib-text-muted ml-4">"elongation_pct":</span>{" "}
            <span className="text-ib-blue">23</span>,<br />
            <span className="text-ib-text-muted ml-4">"status":</span>{" "}
            <span className="text-ib-lime">"PASSED"</span>,<br />
            <span className="text-ib-text-muted ml-4">"inspector":</span>{" "}
            <span className="text-ib-lime">"AI-QC-Engine v3.2"</span><br />
            <span className="text-ib-text-muted">{"}"}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
